import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import SnSheet from "@shared/components/Grid/SnSheet";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import { getInitForm } from "@shared/utils/CommonUtils";
import { ExcelUploadProps } from "@widgets/commonpopup/excel-upload";
import { showDialog } from "@widgets/CommonDialog";
import { RowNode } from "ag-grid-community";
import { callApi } from "@shared/api/restApi";
import { userInfo } from "node:os";
import { global } from "@shared/libs/Global";

type S01WithModalProps = ExcelUploadProps & {
  modalRef?: React.RefObject<any>;
};

const ExcelUpload_S01_Validate = forwardRef<any, S01WithModalProps>(
  ({ modalRef, ...props }, ref) => {
    const {
      Menuprops,
      grouping,
      compareFields,
      provideDefaults,
      mappings,
      validateRow,
    } = props;

    const { userInfo } = global();
    const gridref1 = useRef<any>(null);

    const [columns1, setColums1] = useState<any[]>([]); // H 컬럼
    const [columns2, setColums2] = useState<any[]>([]); // L 컬럼
    const [sheet1Columns, setSheet1Columns] = useState<any[]>([]); // 엑셀 검증 컬럼
    const [dataSource1, setDataSource1] = useState<any>();
    const [codeDataList, setCodeDataList] = useState<Record<string, string>[]>(
      []
    );

    //#region grid 길이 관련
    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    // any 그대로 사용
    useEffect(() => {
      (async () => {
        const rtn: any = await getInitForm();
        setColums1(rtn.grid.grid1); // H 컬럼
        setColums2(rtn.grid.grid2); // L 컬럼
        setSheet1Columns(rtn.grid.sheet1); // 엑셀 검증 컬럼

        // 최초 화면 오픈 시점에 엑셀 업로드 그리드 초기화

        gridref1.current?.resetGridData?.();
      })();
    }, []);

    // 코드치환 필요 시 주입
    useEffect(() => {
      (gridref1.current as any)?.setCodeDataList?.(codeDataList);
    }, [codeDataList]);

    const Validation = async (updatevalue: boolean): Promise<boolean> => {
      if (gridref1.current.api.getDisplayedRowCount() <= 1) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return true;
      }

      let addedNodes: any[] = [];
      gridref1.current.api.forEachNode((node: RowNode) => {
        addedNodes.push(node);
      });

      // cd_cust + cd_item_cust 추출
      const custItemPairs = addedNodes
        .filter(
          (node: any) =>
            node.data.cd_item_cust != null &&
            String(node.data.cd_item_cust).trim() !== ""
        )
        .map((node: any) => ({
          cd_cust: String(node.data.cd_cust ?? "").trim(),
          cd_item_cust: String(node.data.cd_item_cust).trim(),
        }))
        .filter((row: any) => row.cd_cust !== "" && row.cd_item_cust !== "");

      // 중복 제거 (cd_cust + cd_item_cust 기준)
      const uniquePairs = Array.from(
        new Map(
          custItemPairs.map((r: any) => [`${r.cd_cust}|${r.cd_item_cust}`, r])
        ).values()
      );

      if (uniquePairs) {
        const result = await callApi("SN_PUR_POR_REG_001_getCustItem", {
          cd_firm: userInfo.cd_firm,
          custItemSet: uniquePairs,
        });
        const data = result.data;
        if (data.length > 0) {
          const custItemMap = new Map(
            data.map((r: any) => [
              `${String(r.cd_cust).trim()}|${String(r.cd_item_cust).trim()}`,
              r,
            ])
          );

          addedNodes.forEach((node: any) => {
            const row = node.data;
            if (
              row.cd_item_cust == null ||
              String(row.cd_item_cust).trim() === ""
            ) {
              return;
            }

            const key = `${String(row.cd_cust ?? "").trim()}|${String(
              row.cd_item_cust
            ).trim()}`;

            const matched: any = custItemMap.get(key);
            if (!matched) {
              return; // 매핑 없는 건 다음 단계에서 에러 처리 가능
            }

            node.setDataValue("cd_item", matched.cd_item);
            node.setDataValue("nm_item", matched.nm_item);
            node.setDataValue("dc_item_spec", matched.dc_item_spec);
            node.setDataValue("dc_item_unit", matched.dc_item_unit);
          });
        }
      }

      const isValid = await gridref1.current.validateData(updatevalue);
      if (!isValid) {
        await showDialog("error", "오류내역을 확인해주세요.");
        return false;
      } else {
        return true;
      }
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        gridref1.current.resetGridData();
      }

      if (id == "aButton_Validation") {
        const isValid = await Validation(false);
        if (isValid) {
          await showDialog("success", "오류가 없습니다.");
        }
      }
    };

    // 2) 검증 후 다음: 통과 시 모든 업로드 행을 2단으로 전달
    const handleValidateThenNext = async () => {
      try {
        const isValid = await Validation(false);
        if (!isValid) {
          return;
        }

        if (gridref1.current.getChange().modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return;
        }

        await Validation(true);

        const validRows = collectAllRows();
        if (validRows.length === 0) {
          return;
        }

        let rows = validRows.map((r) => {
          const d1 = (provideDefaults?.(r) || {}) as any; // 초기값
          const out = { ...r, ...d1 };

          // 공통 매핑들(무조건 세팅 + 선택 가공)
          (mappings ?? []).forEach((rule) => {
            const keyStr =
              out?.[rule.key] != null ? String(out[rule.key]).trim() : "";
            const base = rule.map[keyStr]; // ← 문자열 키로 조회

            if (base) {
              Object.assign(out, base); // 매핑값 덮어쓰기
              if (rule.transform) {
                const extra = rule.transform(out, base);
                if (extra && typeof extra === "object")
                  Object.assign(out, extra);
              }
            }
          });

          return out; //  map 콜백 내부에서 반환
        });

        // 행 단위 검증 (에러 있으면 중단)
        if (typeof validateRow === "function") {
          const errors: string[] = [];
          rows.forEach((row, i) => {
            const msg = validateRow(row);
            if (msg) errors.push(`ROW ${i + 1}: ${msg}`);
          });
          if (errors.length > 0) {
            await showDialog("error", errors.join("\n\n"));
            return;
          }
        }

        return {
          validRows: rows,
          headerColumns: columns1,
          detailColumns: columns2,
          grouping,
        };
      } catch (error: any) {}
    };

    // 상수 (빈값 체크용)
    const SKIP_FIELDS = new Set(["__op", "__state", "No.", "no", "rowIndex"]);

    // 1) 필드 목록: sheet1Columns 우선, 없으면 첫 행 키로 폴백 (1회 계산)
    const hasInput = (row: any) => {
      const fields = (sheet1Columns ?? [])
        .map((c: any) => c?.field)
        .filter(Boolean);

      const check = (v: any) =>
        v === 0 || v === false || (v != null && String(v).trim() !== "");

      if (fields.length > 0) {
        return fields.some((f) => check(row[f]));
      }

      // fallback: schema 없을 때만 KeyField 제외
      return Object.entries(row)
        .filter(([k]) => k !== "KeyField")
        .some(([, v]) => check(v));
    };

    const collectAllRows = () => {
      const api = gridref1.current?.api;
      if (!api) return [];
      const out: any[] = [];
      api.forEachNode((node: any) => {
        const r = node?.data;
        if (r && hasInput(r)) out.push({ ...r });
      });
      return out;
    };

    // grid1의 width resize
    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };
    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => {
        return await handleValidateThenNext();
      },
    }));

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={"99%"}
            minWidth={130}
            onResizeStop={() => {}}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: "99%" }}
          >
            <SnSheet
              menuInfo={Menuprops}
              ref={gridref1}
              enableAddRow={true}
              columnConfig={sheet1Columns}
              rowData={dataSource1}
              isEditable={true}
              codeDataList={codeDataList}
              compareFields={compareFields ?? []}
              title={Menuprops.nm_menu}
              header={
                <div>
                  <SnButton
                    text={"초기화"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Init");
                    }}
                  />
                  <SnButton
                    text={"오류체크"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Validation");
                    }}
                  />
                </div>
              }
            />
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);
export default ExcelUpload_S01_Validate;
